import { Route } from "@/routes/(auth)/_headed/funds/basket.$basket_id/index";
import {
  ColumnPinningState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import {
  Button,
  cn,
  DataTable,
  ScrollArea,
  ScrollBar,
} from "web-design-system";
import { formatAsPercentage } from "creso-core/utils";
import { BasketAnalyticsSection } from "@funds/components/baskets/basket-analytics";
import { FundBasketExposureTables } from "@funds/components/baskets/fund-basket-exposure-tables";
import { FundBasketOverlap } from "@funds/components/baskets/fund-basket-overlap";
import { FundBasketReturnsChart } from "@funds/components/baskets/fund-basket-returns-chart";
import { AllocationPie } from "@funds/components/baskets/allocation-pie";
import { columns } from "@funds/components/column";
import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Fund } from "creso-core/types";
import { EditableFieldDef } from "@core/components/table/edit-fields";
import { EditFieldsPopover } from "@core/components/table/edit-fields-popover";
import { useLocalStorage } from "usehooks-ts";
import { FUND_BASKET_STORAGE_KEYS } from "@core/constants";
import { useFundBasket, useFundBasketAnalytics } from "creso-core/hooks";
import { FundBasketClients } from "@funds/components/baskets/fund-basket-client";

function mergeTableColumns<T extends Fund, U extends T>(
  c1: EditableFieldDef<T>[],
  c2: EditableFieldDef<U>[]
) {
  return [...c1, ...c2] as EditableFieldDef<U>[];
}

export function FundBasketOverview() {
  const {
    analytics: defaultAnalytics,
    chartData,
    basket: defaultBasketData,
  } = Route.useLoaderData();

  const { data: basket } = useFundBasket(defaultBasketData.id, {
    initialData: defaultBasketData,
  });

  const { data: analytics } = useFundBasketAnalytics(
    { id: defaultBasketData.id },
    { initialData: defaultAnalytics }
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const fundData = useMemo(() => {
    return (
      analytics?.schemes?.map(({ fund, ...rest }) => ({
        ...fund,
        ...rest,
      })) ?? []
    );
  }, [analytics?.schemes]);

  const tableColumns = useMemo(
    () =>
      mergeTableColumns(columns, [
        {
          id: "allocation_percentage",
          accessorKey: "allocation_percentage",
          header: () => <div className="text-center">Allocation %</div>,
          cell: ({ row }) => (
            <div className="flex items-center justify-end gap-2">
              <span className="font-medium">
                {formatAsPercentage(row.original.allocation_percentage, "-", 1)}
              </span>
              <AllocationPie
                percentage={Number(row.original.allocation_percentage) || 0}
              />
            </div>
          ),
          meta: {
            className: "text-right",
          },
          minSize: 120,
          configurable: false,
          group: "General",
          label: "Allocation %",
        },
      ] as EditableFieldDef<(typeof fundData)[number]>[]),
    []
  );

  const columnPinning = useMemo<ColumnPinningState>(
    () => ({
      left: ["fund_name"],
      right: ["allocation_percentage"],
    }),
    []
  );

  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(
      FUND_BASKET_STORAGE_KEYS.ColumnVisibility,
      {
        ...tableColumns.reduce(
          (acc, c) => ({ ...acc, [c.id as string]: false }),
          {}
        ),
        fund_name: true,
        allocation_percentage: true,
        fund_level_category_name: true,
        assets_under_management: true,
        performance__return_3_year: true,
        rankings__category_rank: true,
      }
    );

  const [columnOrder, setColumnOrder] = useLocalStorage(
    FUND_BASKET_STORAGE_KEYS.ColumnOrder,
    tableColumns.map((c) => c.id as string)
  );

  const table = useReactTable({
    data: fundData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination,
      columnPinning,
      columnOrder,
      columnVisibility,
    },
    manualSorting: false,
    onPaginationChange: setPagination,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: false,
  });

  const editColumnConfig = useMemo(() => {
    return tableColumns
      .map((c) => {
        return {
          id: c.id as string,
          label: c.label,
          visible: columnVisibility[c.id as keyof VisibilityState],
          group: c.group,
          configurable: c.configurable ?? true,
        };
      })
      .toSorted(
        (a, b) =>
          columnOrder.indexOf(a.id as string) -
          columnOrder.indexOf(b.id as string)
      );
  }, [columnVisibility, columnOrder, tableColumns]);

  if (!analytics || !basket) return null;

  const { top_holdings, top_sectors, overlap } = analytics;

  return (
    <>
      <FundBasketClients basketId={basket.id} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <span className="text-base font-semibold">Funds in Basket</span>
          <EditFieldsPopover
            columnsConfig={editColumnConfig}
            onReorder={setColumnOrder}
            onVisibilityChange={(values) =>
              setColumnVisibility((prev) => ({ ...prev, ...values }))
            }
          />
        </div>
        <div className="flex flex-col overflow-hidden border border-border rounded-lg">
          <ScrollArea className="flex-1">
            <DataTable
              headerClassName="relative"
              className="border-none"
              pagination={false}
              table={table}
            />
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          {table.getRowCount() > 5 && (
            <div className="flex justify-center border-t border-border rounded-b-lg bg-card dark:bg-background w-full">
              <Button
                size="lg"
                className="font-semibold w-full bg-transparent rounded-t-none"
                variant="ghost"
                onClick={() => {
                  const { pageSize } = table.getState().pagination;
                  setPagination((p) => ({
                    ...p,
                    pageSize: pageSize === 5 ? Number.MAX_SAFE_INTEGER : 5,
                  }));
                }}
              >
                <ChevronDown
                  className={cn(
                    table.getState().pagination.pageSize !== 5 && "rotate-180"
                  )}
                />
                {table.getState().pagination.pageSize !== 5
                  ? "View less"
                  : "View more"}
              </Button>
            </div>
          )}
        </div>
      </div>
      <FundBasketReturnsChart id={analytics.basket.id} chartData={chartData} />
      <BasketAnalyticsSection
        buckets={analytics.analytics?.allocation_breakdown?.buckets}
      />
      <FundBasketExposureTables
        top_holdings={top_holdings}
        top_sectors={top_sectors}
      />
      <FundBasketOverlap basket={basket} overlap={overlap} />
    </>
  );
}
